import _ from 'lodash';
import moment from 'moment';
import Delta from 'quill-delta';
import { Trans, useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import { HistoryIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Avatar, Empty, Paragraph, Skeleton, tokens } from '@treams/treams-ui';

const Timestamp = styled(Paragraph)`
	color: ${tokens.text.tertiary};
	font-size: ${tokens.typography.sizes.small}px;
`;

function GoalAuditRecord(props: { record: graphql.AuditTrail }) {
	const { localize } = useLocalize();

	function serialize(value?: graphql.AuditValue): string | undefined {
		if (!value || !_.isNil(value.booleanValue)) {
			return undefined;
		}

		if (value.dateValue) {
			return moment(value.dateValue).format('L');
		}

		if (value.stringValue) {
			return value.stringValue;
		}

		if (value.localizedStringValue) {
			return localize(value.localizedStringValue)?.text;
		}

		if (value.percentageValue) {
			return (value.percentageValue * 100).toFixed() + '%';
		}

		if (value.quillValue) {
			const quill = value.quillValue as unknown as Delta;
			return quill.ops.map((op) => op.insert ?? '').join('');
		}
	}

	const type = props.record.oldValue
		? props.record.newValue
			? props.record.newValue.booleanValue === true
				? 'on'
				: props.record.newValue.booleanValue === false
					? 'off'
					: 'update'
			: 'remove'
		: 'add';

	const key = [`audit.records.summary.goal`, props.record.field, type].join('.');

	return (
		<Flex my={`${tokens.gap.list}px`} alignItems="flex-start">
			<Box mr={`${tokens.gap.list}px`} flexGrow={0} flexShrink={0}>
				<Avatar user={props.record.createdBy} size={32} />
			</Box>
			<Box flexGrow={1} flexShrink={1} minWidth={0}>
				<Paragraph mb={`${tokens.gap.inline}px`} style={{ whiteSpace: 'pre-wrap' }}>
					<Trans
						i18nKey={key}
						values={{
							firstName: props.record.createdBy.firstName,
							oldValue: serialize(props.record.oldValue ?? undefined),
							newValue: serialize(props.record.newValue ?? undefined),
						}}
						components={{
							italic: <i />,
							bold: <strong />,
							strike: <span style={{ textDecoration: 'line-through' }} />,
						}}
					/>
				</Paragraph>
				<Timestamp>{moment(props.record.createdAt).fromNow()}</Timestamp>
			</Box>
		</Flex>
	);
}

GoalAuditRecord.Skeleton = function GoalAuditRecordSkeleton() {
	return (
		<Flex my={`${tokens.gap.list}px`}>
			<Box mr={`${tokens.gap.list}px`} flexGrow={0} flexShrink={0}>
				<Avatar.Skeleton size={32} />
			</Box>
			<Box flexGrow={1} flexShrink={1}>
				<Paragraph.Skeleton lines={1} mb={`${tokens.gap.inline}px`} />
				<Paragraph.Skeleton lines={1} />
			</Box>
		</Flex>
	);
};

export default function GoalAuditTrail(props: { goalID: string }) {
	const { t } = useTranslation();

	const query = graphql.useGoalAuditTrailQuery({ variables: { goalID: props.goalID } });

	if (!query.data) {
		return (
			<Skeleton.Repeat count={3}>
				<GoalAuditRecord.Skeleton />
			</Skeleton.Repeat>
		);
	}

	if (query.data.goal.auditTrail.edges.length === 0) {
		return (
			<Skeleton.Empty
				contents={
					<Empty
						icon={<HistoryIcon />}
						title={t('goals.show.audit.empty.title')}
						description={t('goals.show.audit.empty.description')}
					/>
				}
			>
				<Skeleton.Repeat count={3}>
					<GoalAuditRecord.Skeleton />
				</Skeleton.Repeat>
			</Skeleton.Empty>
		);
	}

	return (
		<>
			{query.data.goal.auditTrail.edges
				.slice()
				.reverse()
				.map(({ node }) => (
					<GoalAuditRecord key={node.id} record={node as graphql.AuditTrail} />
				))}
		</>
	);
}
